import { useLocation } from 'react-router-dom';
import Spinner from 'components/common/elements/Spinner';
import styles from 'styles/components/common/lists/Table.module.scss';
import classNames from 'classnames';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ReactComponent as EmojiSadIcon } from 'assets/icons/emoji_sad.svg';
import { ReactComponent as SortUpIcon } from 'assets/icons/sort_up.svg';
import { ReactComponent as SortDownIcon } from 'assets/icons/sort_down.svg';
import { TTableColumn, TTablePaging } from 'types/components/table';
import { getScoreLabel, getScoreColor } from 'utils/score';
import { telFormatter } from 'utils/tel';

type TTableProps<T> = {
  data: T[];
  columns: TTableColumn[];
  paging?: TTablePaging;
  sortBy?: string;
  sortDirection?: string;
  onClickSort?: (label: string) => void;
  onClickRow?: (row: T) => void;
  onClickRowById?: (id: string) => void;
  renderActions?: (row: T, headerIndex?: number) => JSX.Element | null;
  idField?: keyof T;
  emptyMessage?: string;
  isLoading?: boolean;
  isStatistics?: boolean;
};

const TableForm = <T,>({
  data,
  columns,
  paging,
  sortBy,
  sortDirection,
  onClickSort,
  onClickRow,
  onClickRowById,
  renderActions,
  idField,
  emptyMessage,
  isLoading,
  isStatistics,
}: TTableProps<T>) => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const listNumbering = (index: number) => {
    const size = query.get('size') ? Number(query.get('size')) : null;
    const currentPage = query.get('page') ? Number(query.get('page')) : null;

    return paging?.totalPages && size && currentPage
      ? paging.totalElements % size === 0
        ? (paging.totalPages - currentPage + 1) * size - index
        : (paging.totalPages - currentPage) * size +
            (paging.totalElements % size) -
            index >
          0
        ? (paging.totalPages - currentPage) * size +
          (paging.totalElements % size) -
          index
        : ''
      : data.length - index;
  };

  return (
    <table className={styles.table}>
      <thead>
        <tr className={styles.tr}>
          {columns.map((column, index) => {
            return (
              <th
                key={index}
                className={classNames(
                  styles.th,
                  column.align && styles[column.align],
                  column.onClick && styles.pointer
                )}
                style={{
                  width: column.width,
                }}
                onClick={
                  onClickSort
                    ? () => column.onClick && onClickSort(column.label)
                    : undefined
                }
              >
                <span>{column.label}</span>

                {column.onClick && (
                  <div className={styles.arrow_container}>
                    <SortUpIcon
                      className={classNames(styles.arrow, styles.arrow_up)}
                      fill={
                        sortDirection === 'asc' && column.name === sortBy
                          ? STYLES_VARIABLES.GRAY_08
                          : STYLES_VARIABLES.GRAY_06
                      }
                    />
                    <SortDownIcon
                      className={classNames(styles.arrow, styles.arrow_down)}
                      fill={
                        sortDirection === 'desc' && column.name === sortBy
                          ? STYLES_VARIABLES.GRAY_08
                          : STYLES_VARIABLES.GRAY_06
                      }
                    />
                  </div>
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {data && data.length > 0 ? (
          data.map((row, rowIndex) => (
            <tr key={rowIndex} className={styles.tr}>
              {columns.map((header, headerIndex) => {
                const cellValue = row[header.name as keyof T];
                return (cellValue !== undefined &&
                  // 연습용 차트 목록 > 차트명에만 수정 버튼이 있어 renderActions로 처리
                  header.name !== 'chartName') ||
                  isStatistics ? (
                  <td
                    key={headerIndex}
                    className={classNames(
                      styles.td,
                      header.align && styles[header.align],
                      idField &&
                        row[idField] &&
                        (onClickRow || onClickRowById) &&
                        styles.pointer,
                      isStatistics && styles[`rank${row['rank' as keyof T]}`]
                    )}
                    onClick={
                      idField && (onClickRow || onClickRowById)
                        ? () => {
                            const id = row[idField]?.toString();
                            if (id) {
                              onClickRow && onClickRow(row);
                              onClickRowById && onClickRowById(id);
                            }
                          }
                        : undefined
                    }
                  >
                    <p
                      style={{
                        width: header.width,
                      }}
                      className={styles.p}
                    >
                      {!isStatistics &&
                      (header.name.includes('Score') ||
                        header.name.includes('score') ||
                        header.name === 'score') ? (
                        <strong
                          style={{
                            color:
                              STYLES_VARIABLES[
                                getScoreColor(cellValue as string | number)
                              ],
                          }}
                        >
                          {cellValue
                            ? getScoreLabel(cellValue as string | number)
                            : '-'}
                        </strong>
                      ) : header.name === 'phoneNum' ? (
                        telFormatter(cellValue?.toString() || '-')
                      ) : header.name === 'rank' && !cellValue ? (
                        '-'
                      ) : (
                        cellValue?.toString() || '-'
                      )}
                      {isStatistics &&
                        header.name.includes('Count') &&
                        !!cellValue?.toString() && <>명</>}
                      {isStatistics &&
                        header.name.includes('Score') &&
                        !!cellValue?.toString() && <>점</>}
                    </p>
                  </td>
                ) : (
                  <td
                    key={headerIndex}
                    className={classNames(
                      styles.td,
                      (onClickRow || onClickRowById) && styles.pointer
                    )}
                    onClick={
                      onClickRow
                        ? () => onClickRow(row)
                        : idField && onClickRowById
                        ? () => {
                            const id = row[idField]?.toString();
                            if (id) {
                              onClickRowById(id);
                            }
                          }
                        : undefined
                    }
                  >
                    {header.name === 'no'
                      ? listNumbering(rowIndex)
                      : renderActions
                      ? renderActions(row, headerIndex)
                      : '-'}
                  </td>
                );
              })}
            </tr>
          ))
        ) : isLoading ? (
          <tr>
            <td colSpan={columns.length} className={styles.loading_wrapper}>
              <Spinner />
            </td>
          </tr>
        ) : (
          <tr>
            <td colSpan={columns.length} className={styles.emoji_wrapper}>
              <EmojiSadIcon width={96} height={96} />
              <p className={styles.text}>{emptyMessage}</p>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default TableForm;
